import { Vocabulary, whatIsOn, within } from '../buildings/Site';
import { HexCellData } from '../map/HexSurface';
import { axialKey, Axial } from '../map/HexCoordinates';

/**
 * Jak blisko jest to, co słychać.
 *
 * How loud a wood or the sea should be is a question about the picture, not
 * about the rules, so it is answered here from the layers this client already
 * holds — the same reasoning that has terrain colour read the biome and the
 * forest layer on this side rather than asking the server what colour a cell is.
 *
 * **What counts as a wood is not decided here.** `whatIsOn` is the function the
 * placement rules use to read a cell, and it is the one asked, so `OBJ_FOREST`
 * means in a sound exactly what it means in "a lumberjack stands beside wood".
 * The alternative was a second reading of the forest layer living in a sound
 * module, drifting quietly from the first.
 *
 * **A share of the ring, not the nearest hit.** Standing next to one tree and
 * standing in a forest are different places and should not sound the same;
 * a count over the ring says which one this is, where a distance to the closest
 * cell would call both of them "right here".
 */

/**
 * How much of the ring around a cell carries this key, from 0 to 1.
 *
 * Cells that have not been streamed in yet are **not counted as empty** — they
 * are left out of both halves of the fraction. Ground nobody has read is not
 * ground with nothing on it, which is the same distinction the placement rules
 * make when they answer `unknown` rather than `no`; here it keeps a wood from
 * fading out because the map behind it has not arrived.
 */
export function nearness(
  world: Map<number, HexCellData>,
  vocabulary: Vocabulary,
  at: Axial,
  key: string,
  radius: number
): number {
  let read = 0;
  let carrying = 0;

  for (const cell of within(at, radius)) {
    const data = world.get(axialKey(cell.q, cell.r));
    if (!data) continue;
    read += 1;
    if (whatIsOn(vocabulary, data, undefined).includes(key)) carrying += 1;
  }

  if (read === 0) return 0;

  // Square-rooted, because loudness is not linear in area: a ring one part in
  // nine wood is audibly a wood nearby, and a straight fraction would have it
  // at a tenth of the volume of standing inside one.
  return Math.sqrt(carrying / read);
}
